import React, { useCallback, useEffect, useState, ReactNode } from 'react';
import { translations as modularTranslations } from '../translations';
import { LanguageContext, type Language } from './languageContextDef';

const LANGUAGE_STORAGE_KEY = 'pignon-rouge.language';

function readStoredLanguage(): Language {
  try {
    const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (raw === 'fr' || raw === 'en' || raw === 'zh') return raw;
  } catch {
    /* ignore */
  }
  return 'fr';
}

// Translation keys
// Note: Currently using modular translations from src/translations/
// Old translations below are kept for reference during migration
// TODO: Remove old translations once all are migrated to modular structure
const remainingTranslations = {
  fr: {
    // Demo Page
    'demo.title': 'Pourvoirie Pignon Rouge',
    'demo.subtitle': 'Démonstration des portails client et gestion',
    'demo.choose': 'Choisissez un portail pour commencer la démonstration',
    'demo.client.title': 'Portail Client',
    'demo.client.subtitle': 'Interface pour les clients de la pourvoirie',
    'demo.client.feature1': 'Parcourir les hébergements',
    'demo.client.feature2': 'Découvrir les activités',
    'demo.client.feature3': 'Réserver en ligne',
    'demo.client.feature4': 'Gérer ses réservations',
    'demo.client.access': 'Accéder au portail client',
    'demo.client.account': 'Compte: client@example.com / client123',
    'demo.manager.title': 'Portail Gestion',
    'demo.manager.subtitle': 'Interface pour la gestion de la pourvoirie',
    'demo.manager.feature1': 'Tableau de bord complet',
    'demo.manager.feature2': 'Gestion des hébergements',
    'demo.manager.feature3': 'Suivi des réservations',
    'demo.manager.feature4': 'Rapports et analyses',
    'demo.manager.access': 'Accéder au portail gestion',
    'demo.manager.account': 'Compte: manager@pignonrouge.com / manager123',
    'demo.guest.title': 'Mode Invité',
    'demo.guest.subtitle': 'Explorez sans compte',
    'demo.guest.feature1': 'Parcourir les hébergements',
    'demo.guest.feature2': 'Voir les activités',
    'demo.guest.feature3': 'Découvrir les forfaits',
    'demo.guest.feature4': 'Pas de réservation',
    'demo.guest.access': 'Explorer en mode invité',
    'demo.landing.title': 'Page d\'accueil',
    'demo.landing.subtitle': 'Site web public de la pourvoirie',
    'demo.landing.feature1': 'Présentation des hébergements',
    'demo.landing.feature2': 'Catalogue des activités',
    'demo.landing.feature3': 'Forfaits et promotions',
    'demo.landing.feature4': 'Informations de contact',
    'demo.landing.access': 'Voir la page d\'accueil',
    'demo.landing.description': 'Site web public - Aucune connexion requise',
    'demo.features.title': 'Fonctionnalités principales',
    'demo.features.accommodations': 'Hébergements',
    'demo.features.accommodations.desc': '8 chalets et yourtes avec descriptions détaillées, photos et disponibilité en temps réel',
    'demo.features.activities': 'Activités',
    'demo.features.activities.desc': 'Pêche, chasse, kayak, randonnée et activités hivernales avec réservation intégrée',
    'demo.features.analytics': 'Analytics',
    'demo.features.analytics.desc': 'Rapports détaillés, métriques d\'occupation et analyses de performance',
    'equipment.fishingRod': 'canne à pêche',
    'equipment.bait': 'appâts',
    'equipment.lifeJacket': 'gilet de sauvetage',
    'equipment.kayak': 'kayak',
    'equipment.paddle': 'pagaie',
    'equipment.hikingBoots': 'bottes de randonnée',
    'equipment.binoculars': 'jumelles',
    'equipment.huntingWeapon': 'arme de chasse',
    'equipment.camouflageClothes': 'vêtements de camouflage',
    'equipment.skis': 'skis',
    'equipment.poles': 'bâtons',
    'equipment.boots': 'bottes',
    'equipment.iceDrill': 'perceuse à glace',
    'equipment.shelter': 'abri',

  },
  en: {
    // Demo Page
    'demo.title': 'Pignon Rouge Lodge',
    'demo.subtitle': 'Client and Management Portal Demonstration',
    'demo.choose': 'Choose a portal to start the demonstration',
    'demo.client.title': 'Client Portal',
    'demo.client.subtitle': 'Interface for lodge clients',
    'demo.client.feature1': 'Browse accommodations',
    'demo.client.feature2': 'Discover activities',
    'demo.client.feature3': 'Book online',
    'demo.client.feature4': 'Manage reservations',
    'demo.client.access': 'Access client portal',
    'demo.client.account': 'Account: client@example.com / client123',
    'demo.manager.title': 'Management Portal',
    'demo.manager.subtitle': 'Interface for lodge management',
    'demo.manager.feature1': 'Complete dashboard',
    'demo.manager.feature2': 'Accommodation management',
    'demo.manager.feature3': 'Reservation tracking',
    'demo.manager.feature4': 'Reports and analytics',
    'demo.manager.access': 'Access management portal',
    'demo.manager.account': 'Account: manager@pignonrouge.com / manager123',
    'demo.guest.title': 'Guest Mode',
    'demo.guest.subtitle': 'Explore without an account',
    'demo.guest.feature1': 'Browse accommodations',
    'demo.guest.feature2': 'View activities',
    'demo.guest.feature3': 'Discover packages',
    'demo.guest.feature4': 'No booking required',
    'demo.guest.access': 'Explore as guest',
    'demo.landing.title': 'Landing Page',
    'demo.landing.subtitle': 'Public website for the lodge',
    'demo.landing.feature1': 'Accommodation showcase',
    'demo.landing.feature2': 'Activity catalog',
    'demo.landing.feature3': 'Packages and promotions',
    'demo.landing.feature4': 'Contact information',
    'demo.landing.access': 'View landing page',
    'demo.landing.description': 'Public website - No login required',
    'demo.features.title': 'Main Features',
    'demo.features.accommodations': 'Accommodations',
    'demo.features.accommodations.desc': '8 chalets and yurts with detailed descriptions, photos and real-time availability',
    'demo.features.activities': 'Activities',
    'demo.features.activities.desc': 'Fishing, hunting, kayaking, hiking and winter activities with integrated booking',
    'demo.features.analytics': 'Analytics',
    'demo.features.analytics.desc': 'Detailed reports, occupancy metrics and performance analysis',
    'equipment.fishingRod': 'fishing rod',
    'equipment.bait': 'bait',
    'equipment.lifeJacket': 'life jacket',
    'equipment.kayak': 'kayak',
    'equipment.paddle': 'paddle',
    'equipment.hikingBoots': 'hiking boots',
    'equipment.binoculars': 'binoculars',
    'equipment.huntingWeapon': 'hunting weapon',
    'equipment.camouflageClothes': 'camouflage clothes',
    'equipment.skis': 'skis',
    'equipment.poles': 'poles',
    'equipment.boots': 'boots',
    'equipment.iceDrill': 'ice drill',
    'equipment.shelter': 'shelter',

  },
  zh: {
    // Demo Page
    'demo.title': '红松小屋',
    'demo.subtitle': '客户和管理门户演示',
    'demo.choose': '选择一个门户开始演示',
    'demo.client.title': '客户门户',
    'demo.client.subtitle': '为小屋客户提供的界面',
    'demo.client.feature1': '浏览住宿',
    'demo.client.feature2': '发现活动',
    'demo.client.feature3': '在线预订',
    'demo.client.feature4': '管理预订',
    'demo.client.access': '访问客户门户',
    'demo.client.account': '账户: client@example.com / client123',
    'demo.manager.title': '管理门户',
    'demo.manager.subtitle': '为小屋管理提供的界面',
    'demo.manager.feature1': '完整仪表板',
    'demo.manager.feature2': '住宿管理',
    'demo.manager.feature3': '预订跟踪',
    'demo.manager.feature4': '报告和分析',
    'demo.manager.access': '访问管理门户',
    'demo.manager.account': '账户: manager@pignonrouge.com / manager123',
    'demo.guest.title': '访客模式',
    'demo.guest.subtitle': '无需账户即可探索',
    'demo.guest.feature1': '浏览住宿',
    'demo.guest.feature2': '查看活动',
    'demo.guest.feature3': '发现套餐',
    'demo.guest.feature4': '无需预订',
    'demo.guest.access': '以访客身份探索',
    'demo.landing.title': '首页',
    'demo.landing.subtitle': '小屋的公共网站',
    'demo.landing.feature1': '住宿展示',
    'demo.landing.feature2': '活动目录',
    'demo.landing.feature3': '套餐和促销',
    'demo.landing.feature4': '联系信息',
    'demo.landing.access': '查看首页',
    'demo.landing.description': '公共网站 - 无需登录',
    'demo.features.title': '主要功能',
    'demo.features.accommodations': '住宿',
    'demo.features.accommodations.desc': '8个小屋和蒙古包，配有详细描述、照片和实时可用性',
    'demo.features.activities': '活动',
    'demo.features.activities.desc': '钓鱼、狩猎、皮划艇、徒步旅行和冬季活动，集成预订功能',
    'demo.features.analytics': '分析',
    'demo.features.analytics.desc': '详细报告、入住率指标和性能分析',
    'equipment.fishingRod': '钓鱼竿',
    'equipment.bait': '鱼饵',
    'equipment.lifeJacket': '救生衣',
    'equipment.kayak': '皮划艇',
    'equipment.paddle': '桨',
    'equipment.hikingBoots': '徒步靴',
    'equipment.binoculars': '双筒望远镜',
    'equipment.huntingWeapon': '狩猎武器',
    'equipment.camouflageClothes': '迷彩服',
    'equipment.skis': '滑雪板',
    'equipment.poles': '滑雪杖',
    'equipment.boots': '靴子',
    'equipment.iceDrill': '冰钻',
    'equipment.shelter': '庇护所',

  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('fr');

  useEffect(() => {
    setLanguageState(readStoredLanguage());
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, []);

  /** Stable when `language` is unchanged — avoids breaking consumers that memoize on `t`. */
  const t = useCallback((key: string): string => {
    // Use modular translations only
    const modularValue = modularTranslations[language]?.[key];
    if (modularValue !== undefined) {
      return modularValue;
    }
    // Fallback to remaining translations (demo, equipment)
    const remainingValue = remainingTranslations[language]?.[key as keyof typeof remainingTranslations[typeof language]];
    return remainingValue || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
